import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { validarCupon, crearPedido } from '../api/carrito';
import { PACK_SIZES } from '../api/productos';
import { getDirecciones, crearDireccion } from '../api/direcciones';
import { sendOrderEmail, createCheckoutPreference, warmUpServer } from '../api/checkout';
import { trackEvent } from '../utils/analytics';
import { getButtonStyle } from '../utils/stepButtonStyle';
import CustomerForm from '../components/CustomerForm';
import { LAST_ORDER_STORAGE_KEY } from '../components/CheckoutButton';
import useSEO from '../hooks/useSEO';
import logo from '../logo.jpg';
import { SHIPPING_COST } from '../constants';
import './Carrito.css';
import './Home.css';

// Arma un resumen legible de la personalización de un ítem del carrito
// (nombre del homenajeado, fecha, oración, etc.) para mostrarlo en la
// lista y para el email de confirmación del pedido.
const describePersonalizacion = (personalizacion) => {
  if (!personalizacion || typeof personalizacion !== 'object') return '';
  return Object.values(personalizacion).filter(Boolean).join(' · ');
};

// Los items del carrito no traen slug/categoría (solo producto_id), a
// diferencia del add_to_cart que dispara Customize.jsx -- pero producto_id
// es lo mismo que ya usa LAST_ORDER_STORAGE_KEY para el evento `purchase`
// en Success.jsx, así que usarlo acá es lo que mantiene el funnel
// begin_checkout -> add_shipping_info -> add_payment_info -> purchase
// consistente entre sí (aunque quede desalineado del primer add_to_cart).
const toGaItem = (item) => ({
  item_id: String(item.producto_id),
  item_name: item.nombre,
  price: Number(item.precio),
  quantity: item.cantidad,
});

const Carrito = () => {
  useSEO({
    title: 'Carrito | MiEstampita',
    description: 'Revisá tu carrito, aplicá un cupón y confirmá tu pedido.',
    path: '/carrito',
    noindex: true,
  });

  const { items, total, loading, updateItem, removeItem, refresh } = useCart();
  const { isLoggedIn, user, register } = useAuth();

  // El backend de Render (usado para el email y para crear la preferencia
  // de Mercado Pago) se duerme en el free tier -- se lo despierta apenas se
  // llega al carrito para que ya esté listo cuando el usuario confirme.
  useEffect(() => {
    warmUpServer();
  }, []);

  // Se dispara una sola vez por carga de página (no en cada refresh() del
  // carrito, ej. al cambiar una cantidad) -- por eso el guard con ref en
  // vez de listar `items`/`total` en las dependencias.
  const viewCartTracked = useRef(false);
  useEffect(() => {
    if (loading || items.length === 0 || viewCartTracked.current) return;
    viewCartTracked.current = true;
    trackEvent('view_cart', {
      currency: 'UYU',
      value: total,
      items: items.map(toGaItem),
    });
  }, [loading, items, total]);

  const [cuponCodigo, setCuponCodigo] = useState('');
  const [cuponAplicado, setCuponAplicado] = useState(null);
  const [cuponError, setCuponError] = useState('');
  const [cuponLoading, setCuponLoading] = useState(false);

  // Si hay sesión, el email de una dirección nueva arranca precargado con
  // el de la cuenta -- el usuario lo puede cambiar igual si quiere que la
  // confirmación vaya a otro mail.
  const [customerData, setCustomerData] = useState({
    name: '',
    address: '',
    city: '',
    department: '',
    postalCode: '',
    phone: '',
    email: user?.email || '',
    isValid: false,
  });

  // Direcciones guardadas: solo existen para cuentas con sesión. El
  // backend además exige `direccion_id` para pedidos de usuario logueado
  // (no acepta dirección inline como el invitado) -- por eso, si elige
  // "dirección nueva", esa dirección se crea sí o sí antes del pedido.
  const [direcciones, setDirecciones] = useState([]);
  const [selectedDireccionId, setSelectedDireccionId] = useState('new');
  const [nuevaDireccionAlias, setNuevaDireccionAlias] = useState('');

  useEffect(() => {
    if (!isLoggedIn) {
      setDirecciones([]);
      setSelectedDireccionId('new');
      return;
    }
    getDirecciones()
      .then((items_) => {
        setDirecciones(items_);
        const predeterminada = items_.find((d) => d.predeterminada);
        setSelectedDireccionId(predeterminada ? predeterminada.id : 'new');
      })
      .catch(() => setDirecciones([]));
  }, [isLoggedIn]);

  const usingSavedDireccion = isLoggedIn && selectedDireccionId !== 'new';
  const selectedDireccion = direcciones.find((d) => d.id === selectedDireccionId);

  // Texto de la dirección de envío para mostrar en el paso 3 (en vez de un
  // botón "Modificar dirección" -- para editarla, el usuario reabre el
  // acordeón del paso 2 directamente).
  const envioDireccionTexto = usingSavedDireccion
    ? [selectedDireccion?.direccion, selectedDireccion?.ciudad].filter(Boolean).join(', ')
    : [customerData.address, customerData.city].filter(Boolean).join(', ');

  // Opción de crear cuenta en el momento para un invitado (checkout,
  // paso 2) -- reusa nombre/email ya cargados en customerData.
  const [wantsToRegister, setWantsToRegister] = useState(false);
  const [registerPassword, setRegisterPassword] = useState('');

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderResult, setOrderResult] = useState(null);

  // Checkout en 3 pasos (Carrito -> Envío -> Pago), mismo patrón de
  // Accordion con "paso N habilitado" que ya usa Customize.jsx.
  const [expanded, setExpanded] = useState('step1');
  const [step1Confirmed, setStep1Confirmed] = useState(false);
  const [step2Confirmed, setStep2Confirmed] = useState(false);

  const descuento = cuponAplicado?.descuento || 0;
  const orderTotal = total > 0 ? total - descuento + SHIPPING_COST : 0;
  const direccionReady = usingSavedDireccion || customerData.isValid;
  const registerReady = !wantsToRegister || registerPassword.trim().length >= 6;

  const isStep2Enabled = step1Confirmed;
  const isStep3Enabled = step2Confirmed;

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    const allowExpand =
      panel === 'step1' || (panel === 'step2' && isStep2Enabled) || (panel === 'step3' && isStep3Enabled);

    if (!allowExpand) return;
    setExpanded(isExpanded ? panel : false);
  };

  // Si el carrito queda vacío estando ya en el paso 2 o 3 (ej. se elimina
  // el único producto), no tiene sentido dejar esos pasos "confirmados".
  useEffect(() => {
    if (items.length === 0) {
      setStep1Confirmed(false);
      setStep2Confirmed(false);
      setExpanded('step1');
    }
  }, [items.length]);

  // GA4 no tiene un evento estándar para "cambiar cantidad" -- se sigue el
  // criterio recomendado de reusar add_to_cart/remove_from_cart con la
  // diferencia (delta) de unidades.
  const handleUpdateCantidad = (item, nuevaCantidad) => {
    const delta = nuevaCantidad - item.cantidad;
    if (delta !== 0) {
      trackEvent(delta > 0 ? 'add_to_cart' : 'remove_from_cart', {
        currency: 'UYU',
        value: Math.abs(delta) * Number(item.precio),
        items: [{ ...toGaItem(item), quantity: Math.abs(delta) }],
      });
    }
    updateItem(item.id, nuevaCantidad);
  };

  const handleRemoveItem = (item) => {
    trackEvent('remove_from_cart', {
      currency: 'UYU',
      value: Number(item.subtotal),
      items: [toGaItem(item)],
    });
    removeItem(item.id);
  };

  const handleAplicarCupon = async () => {
    if (!cuponCodigo.trim()) return;
    setCuponLoading(true);
    setCuponError('');
    try {
      const resultado = await validarCupon({ codigo: cuponCodigo.trim(), subtotal: total });
      setCuponAplicado(resultado);
      trackEvent('cupon_aplicado', {
        codigo: resultado.codigo,
        descuento: resultado.descuento,
      });
    } catch (error) {
      setCuponAplicado(null);
      setCuponError(error.message || 'No se pudo validar el cupón.');
    } finally {
      setCuponLoading(false);
    }
  };

  const handleConfirmarPedido = async () => {
    setOrderError('');
    setPlacingOrder(true);
    try {
      // Si el invitado tildó "crear cuenta", se registra primero -- register()
      // ya lee el carrito de invitado antes de crear la cuenta y vuelve a
      // agregar esos ítems bajo la cuenta nueva, así que para cuando esto
      // termina el pedido ya se puede crear como el de un usuario logueado.
      let effectivelyLoggedIn = isLoggedIn;
      if (!isLoggedIn && wantsToRegister) {
        await register({ nombre: customerData.name, email: customerData.email, password: registerPassword });
        effectivelyLoggedIn = true;
      }

      let direccionId = usingSavedDireccion ? selectedDireccionId : null;

      // Un usuario logueado no puede mandar la dirección inline -- si
      // eligió "dirección nueva", hay que guardarla primero y usar el id
      // que devuelve el backend.
      if (effectivelyLoggedIn && !usingSavedDireccion) {
        const nuevaDireccion = await crearDireccion({
          alias: nuevaDireccionAlias.trim() || 'Envío',
          direccion: customerData,
        });
        direccionId = nuevaDireccion.id;
      }

      const resultado = await crearPedido({
        customerData: direccionId ? undefined : customerData,
        direccionId,
        codigoPromocional: cuponAplicado?.codigo,
      });

      trackEvent('add_payment_info', {
        currency: 'UYU',
        value: resultado.total,
        payment_type: 'mercado_pago',
        items: items.map(toGaItem),
      });

      const effectiveAddress = usingSavedDireccion
        ? {
            name: selectedDireccion.nombre_completo,
            address: selectedDireccion.direccion,
            city: selectedDireccion.ciudad,
            department: selectedDireccion.departamento,
            phone: selectedDireccion.telefono,
            email: selectedDireccion.email,
          }
        : customerData;

      try {
        await sendOrderEmail({
          customerName: effectiveAddress.name,
          customerAddress: [effectiveAddress.address, effectiveAddress.city, effectiveAddress.department]
            .filter(Boolean)
            .join(', '),
          customerPhone: effectiveAddress.phone,
          customerEmail: effectiveAddress.email,
          items: items.map((item) => ({ name: item.nombre, qty: item.cantidad, price: Number(item.precio) })),
          total: resultado.total,
          notes: items
            .map((item) => `${item.nombre} (${item.cantidad}): ${describePersonalizacion(item.personalizacion)}`)
            .join('\n'),
        });
      } catch (emailError) {
        console.error('Error enviando el email de confirmación:', emailError);
      }

      refresh();

      // Checkout Pro: si se puede armar el link de pago, se redirige ahí
      // mismo -- si falla (o el backend de Mercado Pago no responde), se
      // cae al mensaje de "nos vamos a poner en contacto" de siempre, sin
      // bloquear la confirmación del pedido (ya se creó igual).
      try {
        const mpItems = [
          ...items.map((item) => ({
            title: item.nombre,
            quantity: 1, // 1 pack, mismo criterio que el badge del carrito
            unit_price: Number(item.subtotal), // ya es precio unitario × cantidad de copias
            currency_id: 'UYU',
          })),
          { title: 'Envío', quantity: 1, unit_price: SHIPPING_COST, currency_id: 'UYU' },
          ...(descuento > 0 ? [{ title: 'Descuento', quantity: 1, unit_price: -descuento, currency_id: 'UYU' }] : []),
        ];
        const preferencia = await createCheckoutPreference({ items: mpItems });
        if (preferencia?.init_point) {
          try {
            sessionStorage.setItem(
              LAST_ORDER_STORAGE_KEY,
              JSON.stringify({
                currency: 'UYU',
                value: resultado.total,
                items: items.map((item) => ({
                  item_id: item.producto_id,
                  item_name: item.nombre,
                  quantity: item.cantidad,
                })),
              }),
            );
          } catch {
            // sessionStorage puede no estar disponible (ej. navegación privada);
            // /Success simplemente no va a tener detalle para el evento de compra.
          }
          window.location.href = preferencia.init_point;
          return;
        }
      } catch (mpError) {
        console.error('Error creando la preferencia de Mercado Pago:', mpError);
      }

      setOrderResult(resultado);
    } catch (error) {
      setOrderError(error.message || 'No se pudo confirmar el pedido.');
    } finally {
      setPlacingOrder(false);
    }
  };

  if (orderResult) {
    return (
      <div className="home-page">
        <div className="logo-wrapper">
          <Link to="/">
            <img src={logo} alt="MiEstampita Logo" className="logo" />
          </Link>
        </div>
        <div className="home-wrapper">
          <h1 className="selected-title">¡Pedido confirmado!</h1>
          <p>
            {orderResult.mensaje}. Tu número de pedido es <strong>#{orderResult.pedido_id}</strong> por un
            total de <strong>${orderResult.total}</strong>. Nos vamos a poner en contacto para coordinar el
            pago y la entrega.
          </p>
          <Link to="/coleccion" className="back-to-collection">
            Volver a la colección
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="logo-wrapper">
        <Link to="/">
          <img src={logo} alt="MiEstampita Logo" className="logo" />
        </Link>
      </div>
      <div className="home-wrapper">
        <Link to="/coleccion" className="back-to-collection">
          ‹ Seguir comprando
        </Link>
        <h1 className="selected-title">Carrito</h1>

        {loading && <p>Cargando carrito...</p>}

        {!loading && items.length === 0 && (
          <div className="cart-empty">
            <p>Tu carrito está vacío.</p>
            <Link to="/coleccion" className="checkout-button">
              Ver la colección
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            {/* Paso 1 */}
            <Accordion expanded={expanded === 'step1'} onChange={handleChangeAccordion('step1')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>1. Productos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="cart-items">
                  {items.map((item) => (
                    <li key={item.id} className="cart-item">
                      <img src={item.imagen_url} alt={item.nombre} className="cart-item__image" />
                      <div className="cart-item__info">
                        <p className="cart-item__title">{item.nombre}</p>
                        {describePersonalizacion(item.personalizacion) && (
                          <p className="cart-item__personalizacion">
                            {describePersonalizacion(item.personalizacion)}
                          </p>
                        )}
                        <div className="cart-item__qty">
                          <label htmlFor={`qty-${item.id}`}>Cantidad</label>
                          <select
                            id={`qty-${item.id}`}
                            value={item.cantidad}
                            onChange={(event) => handleUpdateCantidad(item, Number(event.target.value))}
                          >
                            {/* Si la cantidad guardada no es una de las opciones vigentes
                                (pedido viejo, cambio de catálogo), se agrega igual para no
                                forzar un cambio de cantidad que el usuario no pidió. */}
                            {(PACK_SIZES.includes(item.cantidad)
                              ? PACK_SIZES
                              : [...PACK_SIZES, item.cantidad].sort((a, b) => a - b)
                            ).map((size) => (
                              <option key={size} value={size}>
                                {size} copias
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="cart-item__actions">
                        <p className="cart-item__subtotal">${item.subtotal}</p>
                        <button
                          type="button"
                          className="cart-item__icon-button"
                          onClick={() => handleRemoveItem(item)}
                          aria-label="Eliminar producto"
                          title="Eliminar"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="step-button"
                  style={getButtonStyle(true)}
                  onClick={() => {
                    trackEvent('begin_checkout', {
                      currency: 'UYU',
                      value: total,
                      items: items.map(toGaItem),
                    });
                    setStep1Confirmed(true);
                    setExpanded('step2');
                  }}
                >
                  Siguiente
                </button>
              </AccordionDetails>
            </Accordion>

            {/* Paso 2 */}
            <Accordion expanded={expanded === 'step2'} onChange={handleChangeAccordion('step2')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: !isStep2Enabled ? 'gray' : 'inherit' }}>
                <Typography>2. Dirección de envío</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {isLoggedIn && direcciones.length > 0 && (
                  <div className="direccion-selector">
                    {direcciones.map((direccion) => (
                      <label key={direccion.id} className="direccion-option">
                        <input
                          type="radio"
                          name="direccion"
                          checked={selectedDireccionId === direccion.id}
                          onChange={() => setSelectedDireccionId(direccion.id)}
                        />
                        <span>
                          <strong>{direccion.alias}</strong> — {direccion.nombre_completo}, {direccion.direccion},{' '}
                          {direccion.ciudad}
                        </span>
                      </label>
                    ))}
                    <label className="direccion-option">
                      <input
                        type="radio"
                        name="direccion"
                        checked={selectedDireccionId === 'new'}
                        onChange={() => setSelectedDireccionId('new')}
                      />
                      <span>Usar una dirección nueva</span>
                    </label>
                  </div>
                )}

                {!usingSavedDireccion && (
                  <>
                    {isLoggedIn && (
                      <div className="direccion-alias">
                        <label htmlFor="alias">Alias para esta dirección (ej: Casa, Trabajo)</label>
                        <input
                          id="alias"
                          type="text"
                          value={nuevaDireccionAlias}
                          onChange={(event) => setNuevaDireccionAlias(event.target.value)}
                          placeholder="Envío"
                        />
                      </div>
                    )}
                    <CustomerForm customer={customerData} onChange={setCustomerData} />
                  </>
                )}

                {!isLoggedIn && (
                  <div className="crear-cuenta-checkout">
                    <label htmlFor="wantsToRegister" className="crear-cuenta-checkout__toggle">
                      <input
                        id="wantsToRegister"
                        type="checkbox"
                        checked={wantsToRegister}
                        onChange={(event) => setWantsToRegister(event.target.checked)}
                      />
                      <span>Crear una cuenta con estos datos para guardar la dirección y ver mis pedidos</span>
                    </label>
                    {wantsToRegister && (
                      <div className="crear-cuenta-checkout__password">
                        <label htmlFor="registerPassword">Contraseña</label>
                        <input
                          id="registerPassword"
                          type="password"
                          value={registerPassword}
                          onChange={(event) => setRegisterPassword(event.target.value)}
                          minLength={6}
                          required
                        />
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className="step-button"
                  style={getButtonStyle(direccionReady && registerReady)}
                  disabled={!direccionReady || !registerReady}
                  onClick={() => {
                    trackEvent('add_shipping_info', {
                      currency: 'UYU',
                      value: total,
                      shipping_tier: 'Estándar',
                      items: items.map(toGaItem),
                    });
                    setStep2Confirmed(true);
                    setExpanded('step3');
                  }}
                >
                  Siguiente
                </button>
              </AccordionDetails>
            </Accordion>

            {/* Paso 3 */}
            <Accordion expanded={expanded === 'step3'} onChange={handleChangeAccordion('step3')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: !isStep3Enabled ? 'gray' : 'inherit' }}>
                <Typography>3. Cupón y confirmación</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className="checkout-step3">
                  <div className="cart-cupon">
                    <label htmlFor="cupon">Código de descuento</label>
                    <div className="cart-cupon__row">
                      <input
                        id="cupon"
                        type="text"
                        value={cuponCodigo}
                        onChange={(event) => setCuponCodigo(event.target.value)}
                        placeholder="Ej: VERANO10"
                      />
                      <button type="button" className="checkout-button" onClick={handleAplicarCupon} disabled={cuponLoading}>
                        {cuponLoading ? 'Validando...' : 'Aplicar'}
                      </button>
                    </div>
                    {cuponError && <p className="cart-cupon__error">{cuponError}</p>}
                    {cuponAplicado && <p className="cart-cupon__success">Cupón {cuponAplicado.codigo} aplicado.</p>}
                  </div>

                  <table className="order-summary">
                    <tbody>
                      <tr>
                        <td>Subtotal</td>
                        <td>${total}</td>
                      </tr>
                      {descuento > 0 && (
                        <tr>
                          <td>Descuento</td>
                          <td>-${descuento}</td>
                        </tr>
                      )}
                      <tr>
                        <td>Envío</td>
                        <td>${SHIPPING_COST}</td>
                      </tr>
                      <tr className="order-summary__total">
                        <td>Total</td>
                        <td>${orderTotal}</td>
                      </tr>
                    </tbody>
                  </table>

                  {envioDireccionTexto && (
                    <p className="checkout-step3__direccion">
                      Se enviará a: <strong>{envioDireccionTexto}</strong>
                    </p>
                  )}

                  <div className="terms-checkbox">
                    <label htmlFor="termsAccepted">
                      <input
                        id="termsAccepted"
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(event) => setTermsAccepted(event.target.checked)}
                      />{' '}
                      Estoy de acuerdo con los{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Terminos y Condiciones
                      </Link>
                    </label>
                  </div>

                  {orderError && (
                    <p className="cart-cupon__error">
                      {orderError}
                      {orderError.includes('cuenta con ese email') && (
                        <>
                          {' '}
                          <Link to="/login">Iniciá sesión</Link>.
                        </>
                      )}
                    </p>
                  )}

                  <div className="step3-actions step3-actions-single">
                    <button
                      type="button"
                      className="checkout-button"
                      onClick={handleConfirmarPedido}
                      disabled={placingOrder || !direccionReady || !termsAccepted}
                    >
                      {placingOrder ? 'Confirmando...' : 'Confirmar Pedido'}
                    </button>
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </div>
    </div>
  );
};

export default Carrito;
